import { MP_REPLAY_ID_HEADER } from '../constants';
import { getReplayId } from '../utils/getReplayId/getReplayId';
import { isSameOrigin } from '../utils/isSameOrigin/isSameOrigin';

const SAME_ORIGIN_FLAG = '__mpReplayIdSameOrigin__';
const HEADER_SET_FLAG = '__mpReplayIdHeaderSet__';
const REPLAY_ID_HEADER_LOWER = MP_REPLAY_ID_HEADER.toLowerCase();

interface TaggedXhr extends XMLHttpRequest {
    [SAME_ORIGIN_FLAG]?: boolean;
    [HEADER_SET_FLAG]?: boolean;
}

/**
 * Tracks methods we have already wrapped, so patching is idempotent without tagging them.
 */
const patchedFns = new WeakSet<object>();

/**
 * Monkey-patches XMLHttpRequest to add the X-MP-REPLAY-Id header on same-origin requests when a
 * replay id is present in sessionStorage. Additive only, idempotent, and fail-safe.
 */
export const patchXhr = (): void => {
    const proto = XMLHttpRequest.prototype;
    if (patchedFns.has(proto.open) || patchedFns.has(proto.setRequestHeader) || patchedFns.has(proto.send)) {
        return;
    }

    const originalOpen = proto.open;
    const originalSetRequestHeader = proto.setRequestHeader;
    const originalSend = proto.send;

    const open: typeof proto.open = function (this: TaggedXhr, method: string, url: string | URL, ...rest: unknown[]) {
        try {
            this[SAME_ORIGIN_FLAG] = isSameOrigin(String(url));
        } catch {
            this[SAME_ORIGIN_FLAG] = false;
        }
        this[HEADER_SET_FLAG] = false;
        // @ts-expect-error forward the remaining optional open() args untouched
        return originalOpen.call(this, method, url, ...rest);
    };
    proto.open = open;
    patchedFns.add(open);

    const setRequestHeader: typeof proto.setRequestHeader = function (this: TaggedXhr, name: string, value: string) {
        try {
            if (typeof name === 'string' && name.toLowerCase() === REPLAY_ID_HEADER_LOWER) {
                this[HEADER_SET_FLAG] = true;
            }
        } catch {
            /* never block the request */
        }
        return originalSetRequestHeader.call(this, name, value);
    };
    proto.setRequestHeader = setRequestHeader;
    patchedFns.add(setRequestHeader);

    const send: typeof proto.send = function (this: TaggedXhr, body?: Document | XMLHttpRequestBodyInit | null) {
        try {
            const id = getReplayId();
            if (id && this[SAME_ORIGIN_FLAG] && !this[HEADER_SET_FLAG]) {
                this.setRequestHeader(MP_REPLAY_ID_HEADER, id);
            }
        } catch {
            /* never block the request */
        }
        return originalSend.call(this, body ?? null);
    };
    proto.send = send;
    patchedFns.add(send);
};
